/**
 * Bookkeeper — autonomous expense-reconciliation workbench (admin/owner).
 *
 * Surfaces the api/bookkeeper agent: shadow/auto toggle, triage focus cards,
 * and an interactive issue list — filter by type, multi-select, ask the AI to
 * SUGGEST a fix (judge for categories, guidance for the rest), APPLY single or
 * in bulk (reversible + learns), open the invoice in Expenses, or reverse a
 * recent auto-fix. Read APIs are admin-gated; mutations post the user's email.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../App";
import { usePermissions } from "../hooks/usePermissions";
import { apiFetch } from "../lib/apiFetch";

const C = {
  bg: "#0d0f12", surface: "#161a20", surface2: "#1e2328", border: "#2a3140",
  accent: "#a5ce3a", accentDim: "rgba(165,206,58,.12)", text: "#e8eaf0",
  muted: "#8892a4", red: "#ef4444", redDim: "rgba(239,68,68,.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,.12)", yellow: "#eab308", blue: "#3b82f6",
};
const money = (n) => `$${(Math.abs(Number(n) || 0)).toFixed(2)}`;

// Group keys for filtering + nice labels.
const GROUPS = { categories: ["category_fix", "missing_category"], receipts: ["jobber_pdf_missing", "pdf_missing"] };
const LABELS = {
  all: "All", categories: "Categories", receipts: "Receipts",
  category_fix: "Category fix", missing_category: "Missing category",
  qb_amount_mismatch: "Amount mismatch", total_mismatch: "Total mismatch",
  tax_missing: "Tax missing", duplicate: "Duplicates", qb_bill_missing: "Deleted QB bill",
  jobber_pdf_missing: "Jobber receipt", pdf_missing: "No receipt",
  fx_mismatch: "FX mismatch", fx_missing: "FX missing", tax_breakdown: "Tax split", ingest_gap: "Ingest gap",
  job_underbilled: "Under-billed job", job_unbilled: "Unbilled job", job_material_overrun: "Material overrun",
  job_low_gp: "Low GP", job_material_no_work: "Material, no work", expense_orphan_po: "Orphan PO",
  expense_unlinked_material: "Unlinked material",
};
const lbl = (k) => LABELS[k] || k;
// Flag types a PDF re-scan can resolve (re-read & reconcile the parsed amounts).
const RESCANNABLE = new Set(["total_mismatch", "tax_breakdown", "tax_missing", "fx_mismatch", "fx_missing"]);

// Diagnose WHY a duplicate group is a duplicate, from its rows.
function dupeWhy(g) {
  const billIds = [...new Set(g.rows.map((r) => r.qb_bill_id).filter(Boolean))];
  const jobIds = [...new Set(g.rows.map((r) => r.jobber_expense_id).filter(Boolean))];
  if (billIds.length >= 2) return { text: `⚠️ ${billIds.length} separate QuickBooks bills — double-counted in QB`, color: C.red };
  if (jobIds.length >= 2) return { text: `Same QB bill, but ${jobIds.length} Jobber expenses — job cost double-counted`, color: C.yellow };
  if (g.sources?.includes("statement_import") && g.sources?.includes("email")) return { text: "Statement import + individual email of the same invoice", color: C.yellow };
  return { text: "Same invoice ingested twice into ElecBid", color: C.muted };
}
function DField({ k, v, vColor }) {
  return <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11.5, lineHeight: 1.7 }}><span style={{ color: C.muted }}>{k}</span><span style={{ color: vColor || C.text, fontWeight: 600, textAlign: "right" }}>{v}</span></div>;
}
function DupeGroup({ g, onOpen, onResolve, busy }) {
  const why = dupeWhy(g);
  // Canonical = the copy with the PDF (prefer), else the email-sourced one.
  const sorted = [...g.rows].sort((a, b) => (b.has_receipt ? 1 : 0) - (a.has_receipt ? 1 : 0) || (a.source === "email" ? -1 : 1));
  const keep = sorted[0], remove = sorted[sorted.length - 1];
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{g.supplier} · {g.invoice_number ? `Inv ${g.invoice_number}` : "no inv #"} · {money(g.amount)} <span style={{ color: C.muted, fontWeight: 400 }}>×{g.count}</span></div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: why.color }}>{why.text}</div>
          {g.count === 2 && keep && remove && keep.id !== remove.id
            ? <Btn size="sm" kind="accent" onClick={() => onResolve(keep.id, remove.id)} disabled={!!busy}>{busy === "dedupe" ? "…" : "Resolve"}</Btn>
            : <span style={{ fontSize: 11, color: C.muted }}>review manually</span>}
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
        {g.rows.map((r, i) => (
          <div key={r.id || i} style={{ flex: "1 1 210px", minWidth: 200, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", padding: "2px 8px", borderRadius: 999, background: C.accentDim, color: C.accent }}>{r.source}</span>
              <span style={{ fontSize: 11, color: C.muted }}>{(r.invoice_date || r.created_at || "").slice(0, 10)}</span>
            </div>
            <DField k="Inv #" v={r.invoice_number || "—"} />
            <DField k="PO" v={r.po_number || "—"} />
            <DField k="QB bill" v={r.qb_bill_id || "—"} />
            <DField k="Jobber" v={r.jobber_expense_id ? "✓ pushed" : "—"} vColor={r.jobber_expense_id ? C.text : C.muted} />
            <DField k="Receipt" v={r.has_receipt ? "✓ PDF" : "✗ none"} vColor={r.has_receipt ? C.green : C.red} />
            <DField k="Status" v={r.status || "—"} />
            <div style={{ marginTop: 8 }}><Btn size="sm" kind="blue" onClick={() => onOpen(r.id)}>Open</Btn></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Btn({ children, onClick, disabled, kind = "ghost", size = "md" }) {
  const k = {
    ghost: { background: "transparent", color: C.muted, border: `1px solid ${C.border}` },
    accent: { background: C.accent, color: "#0d0f12", border: `1px solid ${C.accent}` },
    danger: { background: "transparent", color: C.red, border: `1px solid ${C.red}` },
    blue: { background: "transparent", color: C.blue, border: `1px solid ${C.blue}` },
  };
  const pad = size === "sm" ? "5px 10px" : "9px 16px";
  const fs = size === "sm" ? 12 : 13;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: pad, borderRadius: 8, fontSize: fs, fontWeight: 700, fontFamily: "'Roboto', sans-serif",
      whiteSpace: "nowrap", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1, ...k[kind],
    }}>{children}</button>
  );
}

function StatCard({ label, value, color, active, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: active ? C.accentDim : C.surface, border: `1px solid ${active ? C.accent : (color || C.border)}`,
      borderRadius: 10, padding: "14px 12px", textAlign: "center", cursor: onClick ? "pointer" : "default",
    }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || C.text, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{label}</div>
    </div>
  );
}

function JCList({ title, items, color, render }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: color || C.text, marginBottom: 6 }}>{title} ({items.length})</div>
      {items.slice(0, 15).map((r, i) => <div key={i} style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>{render(r)}</div>)}
    </div>
  );
}
function jobTag(r) { return `#${r.job_num}${r.client ? ` (${r.client})` : ""}`; }

// Read-only job-costing overview: spent (linked expenses) vs quoted (job custom
// fields) vs billed (amount invoiced), off-job purchases, and the week's stock spend.
function JobCostingPanel({ jc, loading, onRefresh }) {
  const s = jc?.summary || {};
  const stock = jc?.stock_week || { by_supplier: [], total: 0, count: 0 };
  const offJob = (s.orphans?.po || 0) + (s.orphans?.unlinked_material || 0);
  const card = (label, value, color) => (
    <div style={{ background: C.surface, border: `1px solid ${value ? (color || C.border) : C.border}`, borderRadius: 10, padding: "12px 10px", textAlign: "center" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: value ? (color || C.text) : C.muted, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10.5, color: C.muted, marginTop: 5 }}>{label}</div>
    </div>
  );
  return (
    <div style={{ marginTop: 26 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingBottom: 6, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>🏗️ Job costing{jc ? <span style={{ color: C.muted, fontWeight: 400 }}> · {jc.open_jobs} open / {jc.closed_jobs} closed scanned</span> : null}</div>
        <Btn size="sm" onClick={onRefresh} disabled={loading}>{loading ? "Loading…" : "Refresh"}</Btn>
      </div>
      {loading && !jc ? <div style={{ color: C.muted, fontSize: 13, padding: "8px 0" }}>Analyzing jobs…</div> : null}
      {jc ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(108px, 1fr))", gap: 10 }}>
            {card("Under-billed", (s.underbilled || []).length, C.red)}
            {card("Closed, unbilled", (s.unbilled || []).length, C.red)}
            {card("Material overruns", (s.overrun || []).length, C.yellow)}
            {card("Low GP", (s.low_gp || []).length, C.yellow)}
            {card("Material, no work", (s.material_no_work || []).length, C.blue)}
            {card("Off-job buys", offJob, C.blue)}
          </div>
          <JCList title="💰 Under-billed (invoiced doesn't cover the job)" items={s.underbilled} color={C.red} render={(r) => `${jobTag(r)} — invoiced ${money(r.invoiced)} of ~${money(r.expected)} ${r.basis}, ${money(r.gap)} unbilled`} />
          <JCList title="🧾 Closed with costs but nothing invoiced" items={s.unbilled} color={C.red} render={(r) => `${jobTag(r)} — ${money(r.cost)} costs, ${money(r.invoiced)} invoiced`} />
          <JCList title="📈 Material over the quoted cost" items={s.overrun} color={C.yellow} render={(r) => `${jobTag(r)} — ${money(r.actual)} actual vs ${money(r.quoted)} quoted, over ${money(r.over)}`} />
          <JCList title="📉 Under the GP floor" items={s.low_gp} color={C.yellow} render={(r) => `${jobTag(r)} — ${r.profit_pct}% GP on ${money(r.revenue)}`} />
          <JCList title="🚚 Material bought, no work logged yet" items={s.material_no_work} color={C.blue} render={(r) => `${jobTag(r)} — ${money(r.material)} material, ~${r.hours}h labour`} />
          {offJob ? <div style={{ fontSize: 12, color: C.muted, marginTop: 12 }}>🔗 {offJob} purchase(s) not tied to a job ({money(s.orphans.dollars)}) — {s.orphans.po} with a PO #, {s.orphans.unlinked_material} from a material supplier. They're in the flag list below as “Orphan PO” / “Unlinked material”.</div> : null}
          <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>📦 Stock purchased this week — {money(stock.total)} <span style={{ color: C.muted, fontWeight: 400 }}>({stock.count} purchases, not on a job)</span></div>
            {(stock.by_supplier || []).length
              ? stock.by_supplier.map((v, i) => <div key={i} style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>{v.supplier} — {money(v.total)}{v.count > 1 ? ` (${v.count})` : ""}</div>)
              : <div style={{ fontSize: 12, color: C.muted }}>None this week.</div>}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default function Bookkeeper() {
  const { session } = useSession() || {};
  const perms = usePermissions() || {};
  const { isAdmin, isOwner } = perms;
  const userEmail = session?.user?.email?.toLowerCase() || "";
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [config, setConfig] = useState(null);
  const [state, setState] = useState({});
  const [triage, setTriage] = useState(null);
  const [flags, setFlags] = useState([]);
  const [fixes, setFixes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [suggestions, setSuggestions] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());
  const [rowBusy, setRowBusy] = useState({});
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const [dupeDetail, setDupeDetail] = useState(null);
  const [dupeLoading, setDupeLoading] = useState(false);
  const [jobCosting, setJobCosting] = useState(null);
  const [jcLoading, setJcLoading] = useState(false);

  const api = useCallback((qs, opts) => {
    const sep = qs.includes("?") ? "&" : "?";
    return apiFetch(`/api/bookkeeper${qs}${sep}userEmail=${encodeURIComponent(userEmail)}`, opts).then((r) => r.json());
  }, [userEmail]);
  const post = useCallback((body) => apiFetch("/api/bookkeeper", {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...body, userEmail }),
  }).then((r) => r.json()), [userEmail]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [st, tr, fl, fx] = await Promise.all([
        api("?action=status"), api("?action=triage"),
        api("?action=list-actions&status=flagged&limit=500"),
        api("?action=list-actions&status=applied&limit=25"),
      ]);
      if (st.error) throw new Error(st.error);
      setConfig(st.config); setState(st.state || {});
      setTriage(tr && !tr.error ? tr : null);
      setFlags((fl && fl.actions) || []);
      setFixes((fx && fx.actions) || []);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { if (userEmail) load(); }, [userEmail, load]);

  // Job-costing overview — loads independently (it's a heavier scan) so it fills in
  // after the main workbench renders.
  const loadJobCosting = useCallback(async () => {
    setJcLoading(true);
    try { const r = await api("?action=job-costing"); setJobCosting(r && r.job_costing ? r.job_costing : (r?.error ? null : r)); }
    catch { /* leave prior */ } finally { setJcLoading(false); }
  }, [api]);
  useEffect(() => { if (userEmail) loadJobCosting(); }, [userEmail, loadJobCosting]);

  const loadDupeDetail = useCallback(async () => {
    setDupeLoading(true);
    try { const r = await api("?action=dupe-detail"); setDupeDetail(r && !r.error ? r : { dupes: [], error: r?.error }); }
    catch (e) { setDupeDetail({ dupes: [], error: e.message }); } finally { setDupeLoading(false); }
  }, [api]);
  // Lazy-load the side-by-side comparison the first time the Duplicates filter is opened.
  useEffect(() => { if (filter === "duplicate" && !dupeDetail && !dupeLoading && userEmail) loadDupeDetail(); }, [filter, dupeDetail, dupeLoading, userEmail, loadDupeDetail]);

  // ── header actions ─────────────────────────────────────────────────────────
  async function toggleMode() {
    const next = config?.mode === "auto" ? "shadow" : "auto";
    if (!window.confirm(`Switch the bookkeeper to ${next.toUpperCase()} mode?${next === "auto" ? " It will start applying allowlisted fixes." : " It will stop changing anything."}`)) return;
    setBusy("toggle"); setMsg(null);
    try { const r = await api(`?action=set-config&mode=${next}`); if (r.error) throw new Error(r.error); setConfig(r.config); setMsg(`Now in ${next.toUpperCase()} mode.`); }
    catch (e) { setMsg("Error: " + e.message); } finally { setBusy(""); }
  }
  async function runNow() {
    setBusy("run"); setMsg("Running a reconciliation scan… (~30s)");
    try { const r = await api("?action=run"); if (r.error) throw new Error(r.error); setMsg(`Scan complete — ${r.summary?.applied || 0} fixed, ${r.summary?.flagged || 0} flagged.`); await load(); }
    catch (e) { setMsg("Error: " + e.message); } finally { setBusy(""); }
  }
  async function selfAudit() {
    setBusy("audit"); setMsg("Running self-audit…");
    try { const r = await api("?action=self-audit"); if (r.error) throw new Error(r.error); setMsg(`Self-audit reviewed ${r.reviewed || 0} fix(es).`); await load(); }
    catch (e) { setMsg("Error: " + e.message); } finally { setBusy(""); }
  }
  async function reverse(scope, label) {
    if (!window.confirm(`Reverse ${label}? This restores the previous value(s) in ElecBid and QuickBooks.`)) return;
    setBusy("rev"); setMsg(null);
    try { const r = await post({ action: "reverse", scope, confirm: true }); if (r.error) throw new Error(r.error); setMsg(`Reversed ${r.total != null ? r.total : ""} action(s).`); await load(); }
    catch (e) { setMsg("Error: " + e.message); } finally { setBusy(""); }
  }

  // ── per-issue actions ──────────────────────────────────────────────────────
  const setRow = (id, v) => setRowBusy((m) => ({ ...m, [id]: v }));
  function openInExpenses(pid) { navigate(`/expenses?focus=${encodeURIComponent(pid)}`); }
  function toggleExpand(id) { setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); }
  function toggleSelect(id) { setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); }

  async function suggestIds(ids) {
    if (!ids.length) return;
    const r = await post({ action: "suggest", ids });
    if (r.error) { setMsg("Error: " + r.error); return; }
    const map = {}; (r.suggestions || []).forEach((s) => { map[s.id] = s; });
    setSuggestions((m) => ({ ...m, ...map }));
    setExpanded((s) => { const n = new Set(s); ids.forEach((id) => n.add(id)); return n; });
  }
  async function suggestOne(id) { setRow(id, "suggest"); try { await suggestIds([id]); } finally { setRow(id, null); } }
  async function suggestSelected() {
    const ids = [...selected]; if (!ids.length) return;
    setBusy("suggest"); setMsg(`Asking the AI about ${ids.length} item(s)…`);
    try { await suggestIds(ids); setMsg(`Got suggestions for ${ids.length} item(s).`); } catch (e) { setMsg("Error: " + e.message); } finally { setBusy(""); }
  }
  async function applyOne(id) {
    setRow(id, "apply");
    try {
      const r = await post({ action: "apply-category", id });
      const res = r.results?.[0] || {};
      if (res.applied) { setMsg(`Applied → ${res.account}.`); setFlags((f) => f.filter((x) => x.id !== id)); setSelected((s) => { const n = new Set(s); n.delete(id); return n; }); await refreshCounts(); }
      else if (res.noop) { setMsg("Already on the correct account."); setFlags((f) => f.filter((x) => x.id !== id)); }
      else setMsg("Couldn’t apply: " + (res.error || r.error || "unknown"));
    } catch (e) { setMsg("Error: " + e.message); } finally { setRow(id, null); }
  }
  async function autoFixSelected() {
    const ids = [...selected].filter((id) => { const f = flags.find((x) => x.id === id); return f && (f.check_type === "category_fix" || f.check_type === "missing_category"); });
    if (!ids.length) { setMsg("Select category items to auto-fix (only categories are auto-applyable)."); return; }
    if (!window.confirm(`Auto-fix ${ids.length} category item(s)? The AI judges each and applies the correct account (reversible).`)) return;
    setBusy("autofix"); setMsg(`Auto-fixing ${ids.length} item(s)…`);
    try {
      const r = await post({ action: "apply-category", ids });
      const ok = new Set((r.results || []).filter((x) => x.applied || x.noop).map((x) => x.id));
      setFlags((f) => f.filter((x) => !ok.has(x.id)));
      setSelected(new Set());
      setMsg(`Applied ${r.applied || 0}${r.noop ? ` · ${r.noop} already correct` : ""}${(r.results || []).some((x) => x.error) ? ` · ${(r.results || []).filter((x) => x.error).length} need review` : ""}.`);
      await refreshCounts();
    } catch (e) { setMsg("Error: " + e.message); } finally { setBusy(""); }
  }
  async function refreshCounts() {
    try { const [tr, fx] = await Promise.all([api("?action=triage"), api("?action=list-actions&status=applied&limit=25")]); setTriage(tr && !tr.error ? tr : null); setFixes((fx && fx.actions) || []); } catch { /* noop */ }
  }
  async function resolveDupe(keepId, removeId) {
    if (!window.confirm("Resolve this duplicate?\n\nKeeps the copy with the PDF. If the redundant QB bill is UNPAID it's deleted from QuickBooks; if it's already paid, nothing is deleted (flagged for you). The removed row is marked 'removed from QB' so you can still review it.")) return;
    setBusy("dedupe"); setMsg(null);
    try {
      const r = await post({ action: "dedupe-resolve", keepId, removeId });
      if (r.ok) setMsg(`✓ ${r.note || r.action}`);
      else setMsg(`Not resolved — ${r.note || r.blocked || r.error || "see detail"}`);
      await loadDupeDetail(); await refreshCounts();
    } catch (e) { setMsg("Error: " + e.message); } finally { setBusy(""); }
  }
  async function rescanOne(id) {
    setRow(id, "rescan");
    try {
      const r = await post({ action: "rescan", id });
      const t = r.rescan?.db_totals;
      if (r.flag_superseded) { setMsg("Re-scan reconciled the totals — fixed."); setFlags((f) => f.filter((x) => x.id !== id)); await refreshCounts(); }
      else if (t && t.reconciles === false) setMsg("Re-scanned, but the PDF's subtotal + tax still don't equal the total — likely a real fee/discount/rounding on the invoice itself. Left flagged.");
      else if (r.rescan?.error) setMsg("Re-scan: " + r.rescan.error);
      else setMsg("Re-scanned — line items refreshed" + (t ? `, totals ${t.applied ? "updated" : "unchanged"}.` : "."));
    } catch (e) { setMsg("Error: " + e.message); } finally { setRow(id, null); }
  }
  async function rescanSelected() {
    const ids = [...selected].filter((id) => { const f = flags.find((x) => x.id === id); return f && RESCANNABLE.has(f.check_type); });
    if (!ids.length) { setMsg("Select total/tax-mismatch items to re-scan (only those can be reconciled from the PDF)."); return; }
    setBusy("rescan"); setMsg(`Re-scanning ${ids.length} PDF(s)…`);
    let fixed = 0, still = 0, errs = 0;
    for (const id of ids) {
      try { const r = await post({ action: "rescan", id }); if (r.flag_superseded) fixed++; else if (r.rescan?.error) errs++; else still++; } catch { errs++; }
    }
    await load(); setSelected(new Set());
    setMsg(`Re-scan done — ${fixed} reconciled & cleared, ${still} still off (real invoice discrepancy), ${errs} couldn't re-scan.`);
    setBusy("");
  }

  if (!isAdmin && !isOwner) {
    return <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontFamily: "'Roboto',sans-serif" }}>Bookkeeper is admin-only.</div>;
  }

  const mode = config?.mode || "shadow";
  const t = triage || {};
  const matches = (f) => filter === "all" ? true : GROUPS[filter] ? GROUPS[filter].includes(f.check_type) : f.check_type === filter;
  const visible = flags.filter(matches);
  const counts = {};
  flags.forEach((f) => { counts[f.check_type] = (counts[f.check_type] || 0) + 1; });
  const catCount = (counts.category_fix || 0) + (counts.missing_category || 0);
  const recCount = (counts.jobber_pdf_missing || 0) + (counts.pdf_missing || 0);
  const chipTypes = Object.keys(counts).filter((ct) => !GROUPS.categories.includes(ct) && !GROUPS.receipts.includes(ct));
  const chips = ["all", ...(catCount ? ["categories"] : []), ...(recCount ? ["receipts"] : []), ...chipTypes];
  const chipCount = (k) => k === "all" ? flags.length : k === "categories" ? catCount : k === "receipts" ? recCount : counts[k];
  const allVisibleSelected = visible.length > 0 && visible.every((f) => selected.has(f.id));
  const wrap = { fontFamily: "'Roboto', sans-serif", color: C.text, maxWidth: 960, margin: "0 auto", padding: "20px 16px 90px" };

  return (
    <div style={wrap}>
      <div style={{ fontSize: 30, fontFamily: "'Bebas Neue', sans-serif", color: C.accent, letterSpacing: "0.04em" }}>BOOKKEEPER</div>
      <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>Autonomous expense reconciliation across ElecBid · Jobber · QuickBooks · AP email.</div>

      {/* Status + controls */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, color: C.muted }}>Mode</span>
            <span style={{ fontSize: 12, fontWeight: 800, padding: "3px 10px", borderRadius: 999, background: mode === "auto" ? C.accentDim : C.surface2, color: mode === "auto" ? C.accent : C.muted, border: `1px solid ${mode === "auto" ? C.accent : C.border}` }}>{mode.toUpperCase()}</span>
            {config && config.enabled === false ? <span style={{ fontSize: 12, color: C.red }}>· DISABLED</span> : null}
          </div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>
            {mode === "auto" ? "Applies allowlisted, AI-judged category fixes automatically (reversible)." : "Watching only — detects & logs, changes nothing."}
            {state?.last_run_at ? ` · last run ${new Date(state.last_run_at).toLocaleString()}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn kind={mode === "auto" ? "ghost" : "accent"} onClick={toggleMode} disabled={busy === "toggle"}>{busy === "toggle" ? "Switching…" : `Switch to ${mode === "auto" ? "Shadow" : "Auto"}`}</Btn>
          <Btn onClick={runNow} disabled={!!busy}>{busy === "run" ? "Running…" : "Run scan"}</Btn>
          <Btn onClick={selfAudit} disabled={!!busy}>{busy === "audit" ? "Auditing…" : "Self-audit"}</Btn>
          <Btn onClick={load} disabled={!!busy}>Refresh</Btn>
          {state?.last_run_id ? <Btn kind="danger" onClick={() => reverse({ runId: state.last_run_id }, "everything from the last run")} disabled={!!busy}>Reverse last run</Btn> : null}
        </div>
      </div>

      {msg ? <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, fontSize: 12.5, background: msg.startsWith("Error") || msg.startsWith("Couldn") ? C.redDim : C.greenDim, color: msg.startsWith("Error") || msg.startsWith("Couldn") ? C.red : C.green, border: `1px solid ${msg.startsWith("Error") || msg.startsWith("Couldn") ? "rgba(239,68,68,.25)" : "rgba(34,197,94,.25)"}` }}>{msg}</div> : null}
      {err ? <div style={{ marginTop: 12, color: C.red, fontSize: 13 }}>Couldn’t load: {err}</div> : null}
      {loading ? <div style={{ marginTop: 24, color: C.muted, fontSize: 13 }}>Loading…</div> : null}

      {!loading && triage ? (
        <>
          {/* Focus cards — click to filter */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginTop: 18 }}>
            <StatCard label="Need your call" value={(t.needs_decision || []).length} color={(t.needs_decision || []).length ? C.yellow : C.border} active={filter === "categories"} onClick={() => setFilter("categories")} />
            <StatCard label="Real $ mismatches" value={(t.real_money_mismatches || []).length} color={(t.real_money_mismatches || []).length ? C.red : C.border} active={filter === "qb_amount_mismatch"} onClick={() => setFilter("qb_amount_mismatch")} />
            <StatCard label="Receipts to attach" value={t.receipts_to_attach?.total || 0} color={C.blue} active={filter === "receipts"} onClick={() => setFilter("receipts")} />
            <StatCard label="Duplicates" value={t.duplicates?.count || 0} active={filter === "duplicate"} onClick={() => setFilter("duplicate")} />
            <StatCard label="Deleted QB bills" value={t.deleted_qb_bills?.count || 0} active={filter === "qb_bill_missing"} onClick={() => setFilter("qb_bill_missing")} />
            <StatCard label="Open flags" value={t.total_flags || 0} active={filter === "all"} onClick={() => setFilter("all")} />
          </div>

          {/* Filter chips */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
            {chips.map((k) => (
              <button key={k} onClick={() => setFilter(k)} style={{
                fontSize: 12, fontWeight: 700, padding: "6px 12px", borderRadius: 999, cursor: "pointer",
                background: filter === k ? C.accent : C.surface2, color: filter === k ? "#0d0f12" : C.muted,
                border: `1px solid ${filter === k ? C.accent : C.border}`,
              }}>{lbl(k)} {chipCount(k) != null ? `(${chipCount(k)})` : ""}</button>
            ))}
          </div>

          {/* Bulk bar */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: C.muted, cursor: "pointer" }}>
              <input type="checkbox" checked={allVisibleSelected} onChange={() => setSelected((s) => { const n = new Set(s); if (allVisibleSelected) visible.forEach((f) => n.delete(f.id)); else visible.forEach((f) => n.add(f.id)); return n; })} />
              Select all shown ({visible.length})
            </label>
            <span style={{ fontSize: 12.5, color: C.muted }}>· {selected.size} selected</span>
            <div style={{ flex: 1 }} />
            <Btn size="sm" onClick={suggestSelected} disabled={!selected.size || !!busy}>{busy === "suggest" ? "Thinking…" : "Suggest selected"}</Btn>
            <Btn size="sm" kind="accent" onClick={autoFixSelected} disabled={!selected.size || !!busy}>{busy === "autofix" ? "Fixing…" : "Auto-fix selected"}</Btn>
            <Btn size="sm" kind="blue" onClick={rescanSelected} disabled={!selected.size || !!busy}>{busy === "rescan" ? "Re-scanning…" : "Re-scan selected"}</Btn>
            {selected.size ? <Btn size="sm" onClick={() => setSelected(new Set())}>Clear</Btn> : null}
          </div>

          {/* Duplicates: side-by-side comparison (instead of the flat list) */}
          {filter === "duplicate" ? (
            <div style={{ marginTop: 12 }}>
              {dupeLoading ? <div style={{ color: C.muted, fontSize: 13, padding: "16px 0" }}>Loading duplicate comparison…</div> : null}
              {dupeDetail && !dupeLoading && (dupeDetail.dupes || []).length === 0 ? <div style={{ color: C.muted, fontSize: 13, padding: "16px 0" }}>No duplicate groups found. 🎉</div> : null}
              {dupeDetail && !dupeLoading && (dupeDetail.dupes || []).length ? (
                <div>
                  <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 8 }}>{dupeDetail.groups} duplicate group(s) · {dupeDetail.rows_involved} rows — compare source, QB bill, Jobber, receipt side by side.</div>
                  {dupeDetail.dupes.map((g, gi) => <DupeGroup key={gi} g={g} onOpen={openInExpenses} onResolve={resolveDupe} busy={busy} />)}
                </div>
              ) : null}
            </div>
          ) : (
          <div style={{ marginTop: 12 }}>
            {visible.length === 0 ? <div style={{ color: C.muted, fontSize: 13, padding: "16px 0" }}>Nothing here — clear of {lbl(filter).toLowerCase()} issues. 🎉</div> : null}
            {visible.map((f) => {
              const sug = suggestions[f.id];
              const open = expanded.has(f.id);
              const isCat = f.check_type === "category_fix" || f.check_type === "missing_category";
              return (
                <div key={f.id} style={{ background: C.surface, border: `1px solid ${selected.has(f.id) ? C.accent : C.border}`, borderRadius: 8, marginBottom: 7 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px" }}>
                    <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggleSelect(f.id)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.45 }}>
                        <span style={{ fontSize: 10.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", marginRight: 6 }}>{lbl(f.check_type)}</span>
                        {f.reason}
                      </div>
                    </div>
                    {f.amount ? <span style={{ fontSize: 12.5, fontWeight: 700, color: C.muted, flexShrink: 0 }}>{money(f.amount)}</span> : null}
                    {RESCANNABLE.has(f.check_type) ? <Btn size="sm" kind="blue" onClick={() => rescanOne(f.id)} disabled={rowBusy[f.id] === "rescan"}>{rowBusy[f.id] === "rescan" ? "Scanning…" : "Re-scan"}</Btn> : null}
                    {f.pending_invoice_id ? <Btn size="sm" kind="blue" onClick={() => openInExpenses(f.pending_invoice_id)}>Open</Btn> : null}
                    <Btn size="sm" onClick={() => (open && sug ? toggleExpand(f.id) : suggestOne(f.id))} disabled={rowBusy[f.id] === "suggest"}>{rowBusy[f.id] === "suggest" ? "…" : open && sug ? "Hide" : "Suggest"}</Btn>
                  </div>
                  {open && sug ? (
                    <div style={{ borderTop: `1px solid ${C.border}`, padding: "10px 12px 12px 38px", background: C.surface2, borderRadius: "0 0 8px 8px" }}>
                      <div style={{ fontSize: 12.5, color: C.text }}>{sug.recommendation}</div>
                      {sug.rationale ? <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>{sug.rationale}{sug.searched ? " · 🔎 web" : ""}{sug.confidence != null ? ` · ${Math.round(sug.confidence * 100)}% sure` : ""}</div> : null}
                      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                        {isCat && sug.fixable ? <Btn size="sm" kind="accent" onClick={() => applyOne(f.id)} disabled={rowBusy[f.id] === "apply"}>{rowBusy[f.id] === "apply" ? "Applying…" : `Apply → ${sug.suggested_account}`}</Btn> : null}
                        {RESCANNABLE.has(f.check_type) ? <Btn size="sm" kind="blue" onClick={() => rescanOne(f.id)} disabled={rowBusy[f.id] === "rescan"}>{rowBusy[f.id] === "rescan" ? "Re-scanning…" : "Re-scan PDF"}</Btn> : null}
                        {f.pending_invoice_id ? <Btn size="sm" kind="blue" onClick={() => openInExpenses(f.pending_invoice_id)}>Open in Expenses</Btn> : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          )}

          {/* Recent auto-fixes */}
          {fixes.length ? (
            <div style={{ marginTop: 26 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8, paddingBottom: 6, borderBottom: `1px solid ${C.border}` }}>✅ Recent auto-fixes ({fixes.length})</div>
              {fixes.map((f) => (
                <div key={f.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 7, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontSize: 12.5, color: C.text }}><span style={{ fontSize: 10.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", marginRight: 6 }}>{lbl(f.check_type)}</span>{f.reason}</div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                    {f.amount ? <span style={{ fontSize: 12.5, fontWeight: 700, color: C.muted }}>{money(f.amount)}</span> : null}
                    <Btn size="sm" kind="danger" onClick={() => reverse({ actionId: f.id }, "this fix")} disabled={!!busy}>Reverse</Btn>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {!loading ? <JobCostingPanel jc={jobCosting} loading={jcLoading} onRefresh={loadJobCosting} /> : null}
    </div>
  );
}
